import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Button from '@mui/material/Button';
import { VersionHistory } from './FigureDetail';

interface ChangeHistoryProps {
  versions: VersionHistory[];
  anchorId?: string;
}

const ChangeHistory: React.FC<ChangeHistoryProps> = ({ versions, anchorId }) => {
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [expandedEntries, setExpandedEntries] = React.useState<number[]>([]);

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const toggleEntry = (versionId: number) => {
    setExpandedEntries((prev) => (prev.includes(versionId) ? prev.filter((id) => id !== versionId) : [...prev, versionId]));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatEdition = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('nb-NO', {
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  return (
    <Box sx={{ mt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
      <Box
        onClick={toggleExpanded}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 28,
          py: 0.25,
          px: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.04)',
          },
        }}>
        <Typography
          variant="subtitle1"
          sx={{
            flexGrow: 1,
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: 400,
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
          }}>
          Endringshistorikk
        </Typography>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            toggleExpanded();
          }}
          sx={{
            p: 0.5,
            ml: 1,
            color: 'rgba(0, 0, 0, 0.54)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </IconButton>
      </Box>

      {expanded && (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '24px 1fr 0.5fr 2fr 0.8fr',
              gap: 2,
              py: 1,
              px: 0,
              bgcolor: 'rgba(0, 0, 0, 0.02)',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            }}>
            <Box />
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(0,0,0,0.6)' }}>
              Utgave
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(0,0,0,0.6)' }}>
              Ver
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(0,0,0,0.6)' }}>
              Tittel
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(0,0,0,0.6)' }}>
              Dato
            </Typography>
          </Box>

          {versions.map((version) => (
            <Box key={version.versionId}>
              <Box
                onClick={() => toggleEntry(version.versionId)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '24px 1fr 0.5fr 2fr 0.8fr',
                  gap: 2,
                  py: 1,
                  px: 0,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleEntry(version.versionId);
                  }}
                  sx={{
                    p: 0,
                    color: 'rgba(0, 0, 0, 0.54)',
                  }}>
                  {expandedEntries.includes(version.versionId) ? (
                    <KeyboardArrowUpIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowDownIcon fontSize="small" />
                  )}
                </IconButton>
                <Typography variant="body2" color="textSecondary">
                  {formatEdition(version.publishedDate)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                  {version.guideVersionPublicId}
                </Typography>
                <Typography variant="body2">{version.title}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatDate(version.publishedDate)}
                </Typography>
              </Box>
              {expandedEntries.includes(version.versionId) && (
                <Box
                  sx={{
                    py: 2,
                    px: 3,
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    flexWrap: 'wrap',
                  }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 700 }}>
                      Utgave
                    </Typography>
                    <Typography variant="body2">{formatEdition(version.publishedDate)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 700 }}>
                      Versjon
                    </Typography>
                    <Typography variant="body2">{version.guideVersionPublicId}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 700 }}>
                      Publisert
                    </Typography>
                    <Typography variant="body2">{formatDate(version.publishedDate)}</Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    size="small"
                    variant="contained"
                    href={`https://byggforsk.no/dokument/${version.docId}?version=${encodeURIComponent(version.guideVersionPublicId)}${
                      anchorId ? `#${anchorId}` : ''
                    }`}
                    target="_blank"
                    rel="noopener noreferrer">
                    Vis versjon
                  </Button>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ChangeHistory;
